import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  NumberInput,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import {
  createPaymentTerm,
  deletePaymentTerm,
  updatePaymentTerm,
  type PaymentTermDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Null to create. */
  term: PaymentTermDto | null;
  onSaved: () => void;
}

interface FormState {
  paymentTermCode: string;
  displayName: string;
  netDays: number | '';
  isActive: boolean;
}

/**
 * Creates or edits one payment term.
 *
 * Delete is offered only for a term no supplier is on. Once one is, deactivating is the answer: the
 * term leaves the picker and the suppliers already on it keep reading correctly. The current
 * product deletes unconditionally, which leaves those suppliers pointing at nothing.
 */
export function PaymentTermFormModal({ opened, onClose, term, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = term !== null;

  const [form, setForm] = useState<FormState>({
    paymentTermCode: '',
    displayName: '',
    netDays: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setForm({
      paymentTermCode: term?.paymentTermCode ?? '',
      displayName: term?.displayName ?? '',
      netDays: term?.netDays ?? '',
      isActive: term?.isActive ?? true,
    });
  }, [opened, term]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        paymentTermCode: form.paymentTermCode.trim().toUpperCase(),
        displayName: form.displayName.trim(),
        // Empty means "this term implies no due date", which is every term the current product has,
        // since it stores them as free text. Not the same as zero, which means due on receipt.
        netDays: form.netDays === '' ? null : Number(form.netDays),
        isActive: form.isActive,
      };
      return isEdit ? updatePaymentTerm(term!.paymentTermId, body) : createPaymentTerm(body);
    },
    onSuccess: () => {
      notifications.show({ message: t('paymentTerms.saved'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  const remove = useMutation({
    mutationFn: () => deletePaymentTerm(term!.paymentTermId),
    onSuccess: () => {
      notifications.show({ message: t('paymentTerms.deleted'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  /** The server's own sentence when it has one — each refusal says what to do instead. */
  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      return err.problem.detail ?? err.problem.title ?? t('paymentTerms.errors.saveFailed');
    }
    return t('paymentTerms.errors.saveFailed');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const next: typeof errors = {};
    if (!form.paymentTermCode.trim()) next.paymentTermCode = t('paymentTerms.errors.codeRequired');
    if (!form.displayName.trim()) next.displayName = t('paymentTerms.errors.descriptionRequired');
    if (form.netDays !== '' && Number(form.netDays) < 0) {
      next.netDays = t('paymentTerms.errors.netDaysNegative');
    }

    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate();
  }

  const canDelete = isEdit && !term!.isInUse;

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('paymentTerms.editTitle', { code: term!.paymentTermCode }) : t('paymentTerms.newTitle')}
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('paymentTerms.code')}
              value={form.paymentTermCode}
              onChange={(e) => setForm({ ...form, paymentTermCode: e.currentTarget.value.toUpperCase() })}
              error={errors.paymentTermCode}
              maxLength={50}
              required
              autoFocus={!isEdit}
            />
            <TextInput
              label={t('paymentTerms.description')}
              description={t('paymentTerms.descriptionHint')}
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.currentTarget.value })}
              error={errors.displayName}
              maxLength={200}
              required
              autoFocus={isEdit}
            />
          </SimpleGrid>

          <NumberInput
            label={t('paymentTerms.netDays')}
            description={t('paymentTerms.netDaysHint')}
            value={form.netDays}
            onChange={(v) => setForm({ ...form, netDays: v === '' ? '' : Number(v) })}
            error={errors.netDays}
            min={0}
            allowDecimal={false}
            w={{ base: '100%', sm: 240 }}
          />

          <Switch
            label={t('paymentTerms.active')}
            description={t('paymentTerms.activeHint')}
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
          />

          {isEdit && term!.isInUse && (
            <Text size="xs" c="dimmed">
              {t('paymentTerms.inUseHint')}
            </Text>
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="space-between">
            {canDelete ? (
              <Button variant="light" color="red" onClick={() => remove.mutate()} loading={remove.isPending}>
                {t('paymentTerms.delete')}
              </Button>
            ) : (
              <span />
            )}
            <Group>
              <Button variant="default" onClick={onClose}>
                {t('shell.cancel')}
              </Button>
              <Button type="submit" color="invetrak" loading={save.isPending}>
                {t('shell.saveChanges')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
